import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { camelCase, upperFirst, mapKeys, snakeCase, isPlainObject } from 'lodash-es';
import { ApiEndpoint } from '@/utils/config';
import { apiFetch } from '../utils/api';
import { isMockData, isProd, logVerbose } from '../utils/config';

const systemFields = ['iv', 'authTag'];
const readOnlyFieldNames = {
  all: [
    'lastLogin', 'state', 'stateTime', 'submitTime',
  ],
  sim: [
    'iccid', 'imsi', 'ipApnList', 'niddApnList', 
    'serviceType', 'provisioningId', 'homeNetwork',
  ],
};
const numericStringFieldNames = ['iccid', 'imsi'];

function pascalCase(str) { return upperFirst(camelCase(str)); }

function isReadOnly(field, modelName) {
  const ro = [
    ...readOnlyFieldNames['all'],
    ...readOnlyFieldNames[modelName] || [],
  ];
  if (ro.includes(field.name)) {
    if (logVerbose) console.debug(`Setting ${modelName}.${field.name} isReadOnly`);
    return true;
  }
  // Return db-native read-only unless it links a foreign key
  if (field.isReadOnly) {
    return typeof field.foreignKey !== 'string';
  }
  // TODO: conditions around default values
  return false;
}

/**
 * Determines if a model field is required
 * @param {object} field 
 * @returns {boolean}
 */
function isRequired(field) {
  if (field.primaryKey === true ||
      field.notNull === true || field.nullable === false) {
    return true;
  }
  return false;
}

/**
 * Converts a schema type to a javascript display type
 * @param {string} fieldType 
 * @returns {string}
 */
function convertType(fieldType) {
  if (fieldType.endsWith('_type') || fieldType.endsWith('Type')) {
    return pascalCase(fieldType);
  } else if (fieldType.startsWith('varchar')) {
    return 'string';
  } else if (['int'].includes(fieldType)) {
    return 'number';
  }
  return fieldType;
}

/**
 * Get a display label for the field or model using its name
 * @param {string} name 
 * @returns {string}
 */
export function getLabel(name) {
  const exclude = [];
  if (exclude.includes(name)) return null;
  const acronyms = [
    'id', 'iccid', 'imsi', 'sim', 'apn', 'cdr', 'ip', 'udp', 'nidd', 'ue',
    'tau', 'act', 'psm', 'edrx', 'rsrp', 'rsrq', 'sinr', 'snr',
    'api', 'url', 'json', 'psk',
  ];
  const words = snakeCase(name).split('_');
  for (let i = 0; i < words.length; i++) {
    if (acronyms.includes(words[i])) {
      words[i] = words[i].toUpperCase();
    } else if (words[i] === 'edrx') {
      words[i] = 'eDRX';
    } else if (words[i] === 'cb') {
      words[i] = 'Callback';
    } else {
      words[i] = upperFirst(words[i]);
    }
  }
  return words.join(' ');
}

function convertDefaultValue(defaultValue) {
  const directConversions = ['string', 'number', 'boolean'];
  if (directConversions.includes(typeof defaultValue)) return defaultValue;
  if (Array.isArray(defaultValue)) return defaultValue;
  if (isPlainObject(defaultValue) && defaultValue.name && defaultValue.args) {
    return `${defaultValue.name}(${defaultValue.args ?? ''})`;
  }
  throw new Error(`Unknown default definition: ${defaultValue}`);
}

export const useSchemaStore = defineStore('schema', () => {
  const schema = ref(null);
  const isLoading = ref(false);
  const error = ref(null);
  const fieldCache = new Map();
  const enumCache = new Map();
  const endpointCache = new Map();

  const allModels = computed(() => schema.value?.models || {});
  const allEnums = computed(() => schema.value?.enums || {});
  
  const getEnumValues = computed(() => (enumName) => {
    return schema.value?.enums?.[enumName] || null;
  });

  const getModelEnums = computed(() => (modelName) => {
    if (enumCache.has(modelName)) return enumCache.get(modelName);
    const model = schema.value?.models?.[modelName];
    if (!model) return [];
    const enums = {};
    for (const field of model) {
      if (field.type?.endsWith('_type') || field.type?.endsWith('Type')) {
        enums[field.type] = getEnumValues.value(field.type);
      }
    }
    enumCache.set(modelName, enums);
    return enums;
  });

  const getFieldMeta = computed(() => (modelName, includeSystem = false) => {
    // Check if we already have this meta-subset cached and/or it exists
    const cacheKey = `${modelName}_${includeSystem}`;    
    if (fieldCache.has(cacheKey)) return fieldCache.get(cacheKey);
    const model = schema.value?.models?.[modelName];
    if (!model) return [];

    // Map to the specific subset of properties
    let meta = model.map(field => ({
      name: field.name,
      type: field.type,
      required: field.required || field.isRequired,
      label: field.label,
      primaryKey: field.primaryKey === true || field.isId === true,
      default: field.default,
      isReadOnly: field.isReadOnly,
      jsonStruct: field.jsonStruct,
    }));

    const embeddedExcludes = systemFields.map(x => pascalCase(x));
    const shouldExclude = (key) => {
      const embeddedKey = pascalCase(key);
      return embeddedExcludes.some(ex => embeddedKey.includes(ex));
    };

    // Optional: Filter out system columns
    if (!includeSystem) {
      meta = meta.filter(field => !shouldExclude(field.name));
    }

    // Cache and return
    fieldCache.set(cacheKey, meta);
    return meta;
  });

  /**
   * Generic getter to extract a specific property from a models's field metadata
   * @param {string} modelName - The model to look up
   * @param {string} [property] - The field property to extract (default: `name`)
   *  (e.g., 'name', 'label', 'type')
   * @param {boolean} [includeSystem] - Whether to include ID/Timestamps
   */
  const getFieldProperty = computed(() => {
    return (modelName, property = 'name', includeSystem = false) => {
      // Get the cached meta array (handles model lookup & system filtering)
      const meta = getFieldMeta.value(modelName, includeSystem);
      // Extract the specific property requested
      // Using optional chaining ensures it won't crash if the property is missing
      return meta.map(field => field[property]);
    };
  });

  /**
   * Generic getter to extract a specific property from a models's field metadata
   * @param {string} modelName - The model to look up
   * @param {boolean} [includeSystem] - Whether to include ID/Timestamps
   */
  const getModelEndpoint = computed(() => (modelName, includeSystem = false) => {
    const cacheKey = `${modelName}_${includeSystem}`;      
    if (endpointCache.has(cacheKey)) return endpointCache.get(cacheKey);

    let endpoint;
    let modelKey = snakeCase(modelName).toUpperCase();
    if (!modelKey.endsWith('S') && !modelKey.startsWith('DEMO')) modelKey += 'S';
    for (const [key, value] of Object.entries(ApiEndpoint)) {
      if (key === modelKey) {
        endpoint = value;
        break;
      }
    }
    endpointCache.set(cacheKey, endpoint);
    return endpoint;
  });

  const getModelPrimaryKey = computed(() => (modelName) => {
    const meta = getFieldMeta.value(modelName);
    for (const field of meta) {
      if (field.primaryKey) return field.name;
    }
    throw new Error(`No primary key found for ${modelName}`);
  });

  async function fetchSchema() {
    // 1. Caching Guard: Return early if we already have it
    if (schema.value) return schema.value;
    
    // 2. Prevent duplicate concurrent requests
    if (isLoading.value) return; 

    isLoading.value = true;
    try {
      let fetched = await apiFetch(ApiEndpoint.SCHEMA);
      if (!fetched) {
        if (!isProd && isMockData) {
          ({ default: fetched } = await import('@/mocks/schema.json'));
        } else {
          throw new Error('Failed to load schema');
        }
      }
      if (fetched.tables) {
        fetched.models = fetched.tables;
        delete fetched.tables;
      }
      enumCache.clear();
      fieldCache.clear();
      endpointCache.clear();
      const enums = mapKeys(fetched.enums, (value, key) => pascalCase(key));
      const models = Object.fromEntries(
        Object.entries(fetched.models).map(([modelName, fields]) => [
          camelCase(modelName),
          fields.map(field => ({
            ...field,
            name: camelCase(field.name),
            label: getLabel(field.name),
            type: convertType(field.type),
            required: field.isRequired ? undefined : isRequired(field),
            default: field.hasDefault ? convertDefaultValue(field.default) : undefined,
            isReadOnly: isReadOnly(field, camelCase(modelName)),
          })),
        ])
      );
      if (logVerbose) {
        console.debug(
          `Updated schema with ${Object.keys(enums).length} enums` +
          ` and ${Object.keys(models).length} models`
        );
      }
      schema.value = { enums, models };
      return schema.value;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    schema,
    isLoading,
    error,
    getLabel,
    fetchSchema,
    getEnumValues,
    getModelEnums,
    getFieldMeta,
    getFieldProperty,
    getModelEndpoint,
    getModelPrimaryKey,
    allEnums,
    allTables: allModels,
  };
});
